/** Layout helpers for the appointments day board (Fresha-style timeline). */

export const DAY_BOARD_SLOT_MINUTES = 15;
/** Proto --slot-h: 22px per 15 min → ~1.466 px/min */
export const DAY_BOARD_PX_PER_MINUTE = 22 / 15;
export const DAY_BOARD_GUTTER_WIDTH = 56;
export const DAY_BOARD_COL_MIN_WIDTH = 168;

/** Locked with prototype: PC never shows more than this many columns. */
export const DESKTOP_MAX_COLS = 4;
export const MOBILE_MAX_COLS = 1;

/** Default PC selection: all if ≤4; else first N with hours that DOW (else first N). */
export function defaultDesktopResourceIds(
  resources: Array<{
    id: string;
    hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
  }>,
  dayOfWeek: number,
  max = DESKTOP_MAX_COLS,
): string[] {
  if (resources.length <= max) return resources.map((r) => r.id);
  const withHours = resources.filter((r) =>
    r.hours.some((h) => h.dayOfWeek === dayOfWeek && h.endTime > h.startTime),
  );
  const pool = withHours.length > 0 ? withHours : resources;
  return pool.slice(0, max).map((r) => r.id);
}

/** Keep selection valid when resources change; never exceed max; allow 1..max. */
export function reconcileDesktopResourceIds(
  selectedIds: string[],
  resources: Array<{
    id: string;
    hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
  }>,
  dayOfWeek: number,
  max = DESKTOP_MAX_COLS,
): string[] {
  const active = new Set(resources.map((r) => r.id));
  const kept = selectedIds.filter((id) => active.has(id)).slice(0, max);
  if (kept.length > 0) return kept;
  return defaultDesktopResourceIds(resources, dayOfWeek, max);
}

export function parseHmToMinutes(hm: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(hm.trim());
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function minutesToHm(total: number): string {
  const h = Math.floor(total / 60);
  const min = total % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Digits-only HH:mm mask (auto-inserts `:`). Rejects letters. */
export function maskHmInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/** Normalize to HH:mm or null if invalid. */
export function normalizeHm(hm: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return minutesToHm(h * 60 + min);
}

export function isValidHm(hm: string): boolean {
  return normalizeHm(hm) != null;
}

export function dateAtMinutes(day: Date, minutes: number): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  d.setMinutes(minutes);
  return d;
}

export function localMinutesFromIso(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

export type DayBoardWindow = { startMin: number; endMin: number };

/** Visible timeline range for the day from resource hours + appointments. */
export function resolveDayBoardWindow(
  dayOfWeek: number,
  resources: Array<{ hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }> }>,
  appointments: Array<{ startsAt: string; endsAt: string }>,
): DayBoardWindow {
  let startMin = 9 * 60;
  let endMin = 18 * 60;
  let found = false;

  for (const r of resources) {
    for (const h of r.hours) {
      if (h.dayOfWeek !== dayOfWeek) continue;
      const s = parseHmToMinutes(h.startTime);
      const e = parseHmToMinutes(h.endTime);
      if (e <= s) continue;
      if (!found) {
        startMin = s;
        endMin = e;
        found = true;
      } else {
        startMin = Math.min(startMin, s);
        endMin = Math.max(endMin, e);
      }
    }
  }

  for (const a of appointments) {
    const s = localMinutesFromIso(a.startsAt);
    const e = localMinutesFromIso(a.endsAt);
    if (e <= s) continue;
    if (!found) {
      startMin = s;
      endMin = e;
      found = true;
    } else {
      startMin = Math.min(startMin, s);
      endMin = Math.max(endMin, e);
    }
  }

  // Snap to slot grid with padding
  startMin = Math.max(0, Math.floor(startMin / DAY_BOARD_SLOT_MINUTES) * DAY_BOARD_SLOT_MINUTES);
  endMin = Math.min(24 * 60, Math.ceil(endMin / DAY_BOARD_SLOT_MINUTES) * DAY_BOARD_SLOT_MINUTES);
  if (endMin <= startMin) {
    startMin = 9 * 60;
    endMin = 18 * 60;
  }
  return { startMin, endMin };
}

export function boardHeightPx(window: DayBoardWindow): number {
  return Math.max(1, window.endMin - window.startMin) * DAY_BOARD_PX_PER_MINUTE;
}

export function blockLayout(
  window: DayBoardWindow,
  startsAt: string,
  endsAt: string,
): { top: number; height: number } {
  const s = Math.max(window.startMin, localMinutesFromIso(startsAt));
  const e = Math.min(window.endMin, localMinutesFromIso(endsAt));
  const top = (s - window.startMin) * DAY_BOARD_PX_PER_MINUTE;
  const height = Math.max(DAY_BOARD_SLOT_MINUTES * DAY_BOARD_PX_PER_MINUTE, (e - s) * DAY_BOARD_PX_PER_MINUTE);
  return { top, height };
}

export function resourceOpenWindows(
  dayOfWeek: number,
  hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>,
): DayBoardWindow[] {
  return hours
    .filter((h) => h.dayOfWeek === dayOfWeek)
    .map((h) => ({ startMin: parseHmToMinutes(h.startTime), endMin: parseHmToMinutes(h.endTime) }))
    .filter((w) => w.endMin > w.startMin)
    .sort((a, b) => a.startMin - b.startMin);
}

export function isMinuteInWindows(minute: number, windows: DayBoardWindow[]): boolean {
  return windows.some((w) => minute >= w.startMin && minute < w.endMin);
}

/** Open hours minus time-off for the local calendar day. */
export function isMinuteBookable(
  minute: number,
  openWins: DayBoardWindow[],
  blockedWins: DayBoardWindow[],
): boolean {
  return isMinuteInWindows(minute, openWins) && !isMinuteInWindows(minute, blockedWins);
}

/**
 * Clip ISO time-off ranges onto a local calendar day as minute windows.
 * Uses device local TZ (same as day board / form slots).
 */
export function timeOffWindowsForLocalDay(
  day: Date,
  offs: Array<{ startsAt: string; endsAt: string }>,
): DayBoardWindow[] {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0, 0);
  const dayStartMs = dayStart.getTime();
  const dayEndMs = dayEnd.getTime();
  const out: DayBoardWindow[] = [];
  for (const o of offs) {
    const s = new Date(o.startsAt).getTime();
    const e = new Date(o.endsAt).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || !(s < dayEndMs && e > dayStartMs)) continue;
    const clipS = Math.max(s, dayStartMs);
    const clipE = Math.min(e, dayEndMs);
    const startMin = Math.floor((clipS - dayStartMs) / 60_000);
    const endMin = Math.ceil((clipE - dayStartMs) / 60_000);
    if (endMin > startMin) out.push({ startMin, endMin });
  }
  return out.sort((a, b) => a.startMin - b.startMin);
}

export type TimeOffBoardEntry = { startMin: number; endMin: number; label: string };

/**
 * Time-off clipped to the local day AND the board window, keeping the reason —
 * feeds the labelled striped absence blocks (proto: "Licencia médica · 14:00–18:00").
 */
export function timeOffEntriesForBoard(
  day: Date,
  window: DayBoardWindow,
  offs: Array<{ startsAt: string; endsAt: string; reason?: string | null }>,
): TimeOffBoardEntry[] {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  const out: TimeOffBoardEntry[] = [];
  for (const o of offs) {
    const s = new Date(o.startsAt).getTime();
    const e = new Date(o.endsAt).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || !(s < dayEnd && e > dayStart)) continue;
    const startMin = Math.max(
      window.startMin,
      Math.floor((Math.max(s, dayStart) - dayStart) / 60_000),
    );
    const endMin = Math.min(
      window.endMin,
      Math.ceil((Math.min(e, dayEnd) - dayStart) / 60_000),
    );
    if (endMin > startMin) out.push({ startMin, endMin, label: o.reason?.trim() || 'Ausencia' });
  }
  return out.sort((a, b) => a.startMin - b.startMin);
}

/**
 * Time-offs that intersect the local calendar day — a multi-day off appears on
 * each day it touches. Keeps the full entry (resourceId, reason) unlike
 * `timeOffWindowsForLocalDay`, which returns clipped minute windows.
 */
export function timeOffsForLocalDay<T extends { startsAt: string; endsAt: string }>(
  day: Date,
  offs: ReadonlyArray<T>,
): T[] {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  return offs.filter((o) => {
    const s = new Date(o.startsAt).getTime();
    const e = new Date(o.endsAt).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && s < dayEnd && e > dayStart;
  });
}

export type MonthDaySummary = {
  /** Non-cancelled turnos del día. */
  count: number;
  /** Marcados no-show (subset de count). */
  noShowCount: number;
  /** Non-cancelled statuses present — feeds the dot row. */
  statuses: Set<string>;
};

/**
 * Per-local-day summary for the month board (cancelled excluded), scoped by
 * resourceIds like the week/day boards. Key = local day `toDateString()`.
 */
export function monthDaySummary(
  appointments: ReadonlyArray<{ resourceId: string; startsAt: string; status: string }>,
  resourceIds?: ReadonlyArray<string>,
): Map<string, MonthDaySummary> {
  const map = new Map<string, MonthDaySummary>();
  for (const a of appointments) {
    if (a.status === 'cancelled') continue;
    if (resourceIds && resourceIds.length > 0 && !resourceIds.includes(a.resourceId)) continue;
    const d = new Date(a.startsAt);
    const key = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toDateString();
    const cur = map.get(key) ?? { count: 0, noShowCount: 0, statuses: new Set<string>() };
    cur.count += 1;
    if (a.status === 'no_show') cur.noShowCount += 1;
    cur.statuses.add(a.status);
    map.set(key, cur);
  }
  return map;
}

export function hasBookableMinutes(
  openWins: DayBoardWindow[],
  blockedWins: DayBoardWindow[],
  step = DAY_BOARD_SLOT_MINUTES,
): boolean {
  for (const w of openWins) {
    for (let m = w.startMin; m < w.endMin; m += step) {
      if (isMinuteBookable(m, openWins, blockedWins)) return true;
    }
  }
  return false;
}

export type DayBoardKpis = {
  /** Turnos del día (sin cancelados). */
  total: number;
  /** Con llegada marcada (sin cancelados). */
  arrived: number;
  /** Marcados no-show. */
  noShows: number;
  /** Minutos ocupados / reservables (%); null si no hay minutos reservables. */
  occupancyPercent: number | null;
};

/**
 * KPI strip del tablero día (proto: turnos / ocupación / llegaron / no vinieron).
 * Ocupación = minutos de turnos no cancelados sobre minutos reservables
 * (horario abierto menos ausencias) de las agendas visibles.
 */
export function computeDayBoardKpis(args: {
  appointments: Array<{
    resourceId: string;
    startsAt: string;
    endsAt: string;
    status: string;
    arrivedAt?: string | null;
  }>;
  resources: Array<{
    id: string;
    hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
  }>;
  timeOffs?: Array<{ resourceId: string; startsAt: string; endsAt: string }>;
  day: Date;
}): DayBoardKpis {
  const dayOfWeek = args.day.getDay();
  const visibleIds = new Set(args.resources.map((r) => r.id));
  const offsByResource = new Map<string, Array<{ startsAt: string; endsAt: string }>>();
  for (const t of args.timeOffs ?? []) {
    const list = offsByResource.get(t.resourceId) ?? [];
    list.push(t);
    offsByResource.set(t.resourceId, list);
  }

  let bookableMin = 0;
  for (const r of args.resources) {
    const openWins = resourceOpenWindows(dayOfWeek, r.hours);
    if (openWins.length === 0) continue;
    const blockedWins = timeOffWindowsForLocalDay(args.day, offsByResource.get(r.id) ?? []);
    for (const w of openWins) {
      let open = w.endMin - w.startMin;
      for (const b of blockedWins) {
        const s = Math.max(b.startMin, w.startMin);
        const e = Math.min(b.endMin, w.endMin);
        if (e > s) open -= e - s;
      }
      bookableMin += Math.max(0, open);
    }
  }

  let total = 0;
  let arrived = 0;
  let noShows = 0;
  let bookedMin = 0;
  for (const a of args.appointments) {
    if (!visibleIds.has(a.resourceId) || a.status === 'cancelled') continue;
    total += 1;
    if (a.arrivedAt) arrived += 1;
    if (a.status === 'no_show') noShows += 1;
    const s = localMinutesFromIso(a.startsAt);
    const e = localMinutesFromIso(a.endsAt);
    if (e > s) bookedMin += e - s;
  }

  return {
    total,
    arrived,
    noShows,
    occupancyPercent:
      bookableMin > 0 ? Math.min(100, Math.round((bookedMin / bookableMin) * 100)) : null,
  };
}

export function hourLabels(window: DayBoardWindow): Array<{ minute: number; label: string }> {
  const out: Array<{ minute: number; label: string }> = [];
  // Clock hours that fall inside the range (e.g. 09:00 … 20:00).
  let m = Math.ceil(window.startMin / 60) * 60;
  for (; m < window.endMin; m += 60) {
    out.push({ minute: m, label: minutesToHm(m) });
  }
  // Closing boundary when the day ends on the hour (e.g. closes 21:00 → show "21:00").
  if (window.endMin % 60 === 0 && (out.length === 0 || out[out.length - 1]!.minute !== window.endMin)) {
    out.push({ minute: window.endMin, label: minutesToHm(window.endMin) });
  }
  return out;
}
